import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  doc as liteDoc,
  getDoc as getLiteDoc,
  getFirestore as getLiteFirestore,
} from "firebase/firestore/lite";
import { useTranslation } from "react-i18next";
import { SignalChart } from "../charts/SignalChart";
import { firebaseApp } from "../../firebase/firebaseApp";
import { useDateTimeFormat } from "../../hooks/useDateTimeFormat";
import { formatValidatedMeasurement } from "../../utils/results/validatedRanges";
import {
  pickSpirometryResult,
  SPIROMETRY_PREDICTION_SOURCES,
} from "../../utils/results/spirometry";
import {
  getPointSeriesMaxSecs,
  normaliseTimeValuePoints,
} from "../../utils/results/viewTimeSeries";
import { pickHeartDxHrBpm, pickHeartDxHRV } from "../../utils/results/heartdx";
import {
  pickHrSignalQuality,
  pickRr,
  pickRrSignalQuality,
} from "../../utils/results/signalQuality";
import { pickCardiopulmonaryDetrendedSignal } from "../../utils/results/cardiopulmonaryView";
import {
  pickPostExertionRrAvg,
  pickPostExertionRrMax,
  pickPostExertionRrMin,
} from "../../utils/results/postExertion";
import FlowVolumeLoopsSection from "./FlowVolumeLoopsSection";

const RECORDING_COLLECTION_CANDIDATES = [
  "RecordingSpirometry",
  "RecordingJapanCOPDLongitudinalStudy",
  "RecordingTidalBreathing",
  "RecordingPostExertion",
  "RecordingManualAudio",
];

const recordingDocumentCache = new Map();
const pendingRecordingDocumentLoads = new Map();
const recordingDocumentsDb = getLiteFirestore(firebaseApp);

function normaliseStatus(value) {
  if (typeof value !== "string") return "unknown";

  const normalized = value.trim().toLowerCase();

  if (
    normalized === "pass" ||
    normalized === "passed" ||
    normalized === "good" ||
    normalized === "very good"
  ) {
    return normalized === "passed" ? "pass" : normalized;
  }

  if (
    normalized === "fail" ||
    normalized === "failed" ||
    normalized === "bad" ||
    normalized === "poor"
  ) {
    return normalized === "failed" ? "fail" : normalized;
  }

  return "unknown";
}

function formatStatusValue(value, t) {
  const normalized = normaliseStatus(value);

  if (normalized === "very good") {
    return t("profilePage.statusVeryGood", "Very good");
  }

  if (normalized === "good") {
    return t("profilePage.statusGood", "Good");
  }

  if (normalized === "pass") {
    return t("profilePage.statusPass", "Pass");
  }

  if (normalized === "fail") {
    return t("profilePage.statusFail", "Fail");
  }

  if (normalized === "bad") {
    return t("profilePage.statusBad", "Bad");
  }

  return t("profilePage.statusUnknown", "Unknown");
}

function classifyRecording(entry, data) {
  const variant = entry?.variant || "";

  if (
    variant === "RecordingSpirometry" ||
    data?.results?.mpm?.spirometry ||
    data?.results?.mpm?.flow_loop?.summary_metrics?.spirometryBreathCount
  ) {
    return "spirometry";
  }

  if (variant === "RecordingPostExertion") {
    return "post-exertion";
  }

  if (variant === "RecordingManualAudio") {
    return "manual-audio";
  }

  return "cardiopulmonary";
}

function formatOverlayMetric(metric, fallback = "--") {
  if (typeof metric === "number" && Number.isFinite(metric)) {
    return String(metric);
  }

  if (typeof metric === "string" && metric.trim()) {
    return metric;
  }

  return fallback;
}

function formatFixedOverlayMetric(metric, decimals = 3, fallback = "--") {
  if (typeof metric === "number" && Number.isFinite(metric)) {
    return metric.toFixed(decimals);
  }

  if (typeof metric === "string" && metric.trim()) {
    return metric;
  }

  return fallback;
}

function toTrendNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function buildRecordingDocumentCacheKey(uid, variant, name) {
  return `${uid}::${variant}::${name}`;
}

async function loadExactRecordingDocument({ db, uid, variant, name }) {
  const cacheKey = buildRecordingDocumentCacheKey(uid, variant, name);
  const documentRef = liteDoc(db, "measurements", uid, variant, name);

  if (recordingDocumentCache.has(cacheKey)) {
    return {
      variant,
      exists: true,
      data: recordingDocumentCache.get(cacheKey),
    };
  }

  if (pendingRecordingDocumentLoads.has(cacheKey)) {
    return pendingRecordingDocumentLoads.get(cacheKey);
  }

  const pendingLoad = getLiteDoc(documentRef)
    .then((snapshot) => {
      if (!snapshot.exists()) {
        return {
          variant,
          exists: false,
          data: null,
        };
      }

      const data = snapshot.data();
      recordingDocumentCache.set(cacheKey, data);

      return {
        variant,
        exists: true,
        data,
      };
    })
    .finally(() => {
      pendingRecordingDocumentLoads.delete(cacheKey);
    });

  pendingRecordingDocumentLoads.set(cacheKey, pendingLoad);
  return pendingLoad;
}

export async function loadPatientRecordingDocument(entry, options = {}) {
  if (!entry?.uid || !entry?.name) {
    throw new Error("recording-not-found");
  }

  const db = options.db || recordingDocumentsDb;
  const candidates = entry?.collectionName
    ? [
        entry.collectionName,
        ...RECORDING_COLLECTION_CANDIDATES.filter(
          (variant) => variant !== entry.collectionName,
        ),
      ]
    : RECORDING_COLLECTION_CANDIDATES;

  for (const variant of candidates) {
    const snapshot = await loadExactRecordingDocument({
      db,
      uid: entry.uid,
      variant,
      name: entry.name,
    });

    if (snapshot.exists) {
      return {
        variant: snapshot.variant,
        data: snapshot.data,
      };
    }
  }

  throw new Error("recording-not-found");
}

export function buildRecordingPresentation(entry, data, t) {
  const kind = classifyRecording(entry, data);

  if (kind === "spirometry") {
    const spirometry = pickSpirometryResult(data, {
      predictionSource: SPIROMETRY_PREDICTION_SOURCES.LUNGDX,
    });
    const fullSignalSeries = [
      {
        label: t("profilePage.rawSignalSeries", "Raw"),
        points: spirometry.processedSignal.signalTracePoints,
        stroke: "#8F8F93",
      },
      {
        label: t("profilePage.detrendedSignalSeries", "Detrended"),
        points: spirometry.processedSignal.detrendedSignalPoints,
        stroke: "#107A92",
      },
    ].filter((seriesItem) => seriesItem.points?.length);

    const spirometrySegmentSeries = [
      {
        label: t("profilePage.rawSignalSeries", "Raw"),
        points: spirometry.processedSignal.spirometrySignalTracePoints,
        stroke: "#B776B9",
      },
      {
        label: t("profilePage.detrendedSignalSeries", "Detrended"),
        points: spirometry.processedSignal.spirometryDetrendedSignalPoints,
        stroke: "#852170",
      },
    ].filter((seriesItem) => seriesItem.points?.length);

    const trendMetrics = [
      {
        key: "meanExhaleInhaleAreaRatio",
        label: t(
          "profilePage.meanExhaleInhaleAreaRatio",
          "Mean Exhale/Inhale Area Ratio",
        ),
        value: formatFixedOverlayMetric(
          spirometry.additionalMetrics?.meanExhaleInhaleAreaRatio,
        ),
        rawValue: toTrendNumber(
          spirometry.additionalMetrics?.meanExhaleInhaleAreaRatio,
        ),
        unit: null,
        trendDecimals: 3,
      },
      {
        key: "meanRectangularAreaRatio",
        label: t(
          "profilePage.meanRectangularAreaRatio",
          "Mean Rectangular Area Ratio",
        ),
        value: formatFixedOverlayMetric(
          spirometry.additionalMetrics?.meanRectangularAreaRatio,
        ),
        rawValue: toTrendNumber(
          spirometry.additionalMetrics?.meanRectangularAreaRatio,
        ),
        unit: null,
        trendDecimals: 3,
      },
    ];

    return {
      title: t("profilePage.recordingKinds.spirometry", "Spirometry"),
      metrics: [
        {
          key: "fev1",
          label: "FEV1",
          value: formatValidatedMeasurement("fev1", spirometry.fev1),
          rawValue: toTrendNumber(spirometry.fev1),
          unit: "L",
          trendDecimals: 2,
        },
        {
          key: "fvc",
          label: "FVC",
          value: formatValidatedMeasurement("fvc", spirometry.fvc),
          rawValue: toTrendNumber(spirometry.fvc),
          unit: "L",
          trendDecimals: 2,
        },
        {
          key: "fev1FvcRatio",
          label: "FEV1/FVC",
          value: formatValidatedMeasurement(
            "fev1FvcRatio",
            spirometry.fev1FvcRatio,
          ),
          rawValue: toTrendNumber(spirometry.fev1FvcRatio),
          unit: null,
          trendDecimals: 2,
        },
        {
          key: "pef",
          label: "PEF",
          value: formatValidatedMeasurement("pef", spirometry.pef),
          rawValue: toTrendNumber(spirometry.pef),
          unit: "L/min",
          trendDecimals: 0,
        },
        {
          key: "rr",
          label: "RR",
          value: formatValidatedMeasurement(
            "rr",
            spirometry.rr?.average ?? spirometry.rr?.mean,
          ),
          rawValue: toTrendNumber(
            spirometry.rr?.average ?? spirometry.rr?.mean,
          ),
          unit: "bpm",
          trendDecimals: 0,
        },
      ],
      statuses: [
        {
          key: "signalQuality",
          label: t("profilePage.signalQuality", "Signal quality"),
          value: formatStatusValue(spirometry.signalQuality, t),
          rawValue: spirometry.signalQuality,
          trendType: "quality",
        },
        {
          key: "spirometryQuality",
          label: t("profilePage.spirometryQuality", "Spirometry quality"),
          value: formatStatusValue(spirometry.spirometryQuality, t),
          rawValue: spirometry.spirometryQuality,
          trendType: "quality",
        },
      ],
      trendMetrics,
      charts: [
        {
          type: "time-series",
          title: t("profilePage.fullSignalTraceTitle", "Full signal trace"),
          subtitle: t(
            "profilePage.fullSignalTraceSubtitle",
            "Raw and detrended traces across the full recording.",
          ),
          series: fullSignalSeries,
          emptyLabel: t(
            "profilePage.noSignalTrace",
            "No signal trace is available for this recording.",
          ),
        },
        {
          type: "time-series",
          title: t("profilePage.spirometrySegmentTitle", "Spirometry segment"),
          subtitle: t(
            "profilePage.spirometrySegmentSubtitle",
            "Raw and detrended traces for the detected spirometry segment.",
          ),
          series: spirometrySegmentSeries,
          emptyLabel: t(
            "profilePage.noSpirometrySegmentSignal",
            "No spirometry segment trace is available for this recording.",
          ),
        },
      ],
    };
  }

  if (kind === "manual-audio") {
    return {
      title: t("profilePage.recordingKinds.manualAudio", "Manual audio"),
      metrics: [],
      statuses: [],
      charts: [],
    };
  }

  const rr = pickPostExertionRrAvg(data) ?? pickRr(data);
  const rrMin = pickPostExertionRrMin(data);
  const rrMax = pickPostExertionRrMax(data);
  const hr = pickHeartDxHrBpm(data);
  const hrv = pickHeartDxHRV(data);
  const rrQuality = pickRrSignalQuality(data);
  const hrQuality = pickHrSignalQuality(data);
  const detrendedSignal = pickCardiopulmonaryDetrendedSignal(data);

  return {
    title: t("profilePage.recordingKinds.cardiopulmonary", "Cardiopulmonary"),
    metrics: [
      {
        key: "hr",
        label: "HR",
        value: formatValidatedMeasurement("hr", hr),
        rawValue: toTrendNumber(hr),
        unit: "bpm",
        trendDecimals: 0,
      },
      {
        key: "hrv",
        label: "HRV",
        value: formatValidatedMeasurement("hrv", hrv),
        rawValue: toTrendNumber(hrv),
        unit: "ms",
        trendDecimals: 0,
      },
      {
        key: "rr",
        label: "RR",
        value: formatValidatedMeasurement("rr", rr),
        rawValue: toTrendNumber(rr),
        unit: "bpm",
        trendDecimals: 0,
      },
      {
        key: "rrMin",
        label: t("profilePage.rrMin", "RR Min"),
        value: formatOverlayMetric(
          formatValidatedMeasurement("rr", rrMin, "--"),
        ),
        rawValue: toTrendNumber(rrMin),
        unit: "bpm",
        trendDecimals: 0,
      },
      {
        key: "rrMax",
        label: t("profilePage.rrMax", "RR Max"),
        value: formatOverlayMetric(
          formatValidatedMeasurement("rr", rrMax, "--"),
        ),
        rawValue: toTrendNumber(rrMax),
        unit: "bpm",
        trendDecimals: 0,
      },
    ],
    statuses: [
      {
        key: "cardiacQuality",
        label: t("profilePage.cardiacQuality", "Cardiac quality"),
        value: formatStatusValue(hrQuality, t),
        rawValue: hrQuality,
        trendType: "quality",
      },
      {
        key: "respiratoryQuality",
        label: t("profilePage.respiratoryQuality", "Respiratory quality"),
        value: formatStatusValue(rrQuality, t),
        rawValue: rrQuality,
        trendType: "quality",
      },
    ],
    charts: [
      {
        type: "time-series",
        title: t(
          "profilePage.respiratorySignalChartTitle",
          "Respiratory signal",
        ),
        subtitle: t(
          "profilePage.respiratorySignalChartSubtitle",
          "Detrended respiratory waveform from the recording.",
        ),
        points: detrendedSignal.points,
        stroke: "#107A92",
        emptyLabel: t(
          "profilePage.noRespiratorySignal",
          "No respiratory signal is available for this recording.",
        ),
      },
    ],
  };
}

function SummaryCard({ label, value, unit }) {
  return (
    <div className="rounded-xl border border-greyscale-1 bg-background px-3 py-2">
      <p className="text-sm text-greyscale-3">{label}</p>
      <div className="flex items-end gap-2">
        <p className="text-2xl font-semibold text-typography">{value}</p>
        {unit ? <p className="pb-1 text-sm text-greyscale-3">{unit}</p> : null}
      </div>
    </div>
  );
}

function TimeSeriesChart({ title, points, emptyLabel, stroke, series }) {
  const seriesList = useMemo(
    () =>
      Array.isArray(series) && series.length
        ? series
        : [
            {
              label: title,
              points,
              stroke,
            },
          ],
    [points, series, stroke, title],
  );

  const normalisedSeries = useMemo(
    () =>
      seriesList
        .map((seriesItem) => ({
          ...seriesItem,
          points: normaliseTimeValuePoints(seriesItem?.points || []),
        }))
        .filter((seriesItem) => seriesItem.points.length),
    [seriesList],
  );

  if (!normalisedSeries.length) {
    return (
      <section className="rounded-xl border border-greyscale-1 bg-container p-4">
        <h3 className="text-lg font-semibold text-typography">{title}</h3>

        <div className="mt-4 rounded-lg bg-background px-4 py-10 text-center text-sm text-greyscale-3">
          {emptyLabel}
        </div>
      </section>
    );
  }

  const maxSecs = normalisedSeries.reduce((maxValue, seriesItem) => {
    return Math.max(maxValue, getPointSeriesMaxSecs(seriesItem.points, 0));
  }, 0);
  const rawSeries = normalisedSeries.length > 1 ? normalisedSeries[0] : null;
  const smoothSeries =
    normalisedSeries.length > 1 ? normalisedSeries[1] : normalisedSeries[0];
  const displaySeries = rawSeries ? [rawSeries, smoothSeries] : [smoothSeries];

  return (
    <section className="rounded-xl border border-greyscale-1 bg-container p-4">
      <h3 className="text-lg font-semibold text-typography">{title}</h3>

      {displaySeries.length > 1 ? (
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-greyscale-3">
          {displaySeries.map((seriesItem) => (
            <div key={seriesItem.label} className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: seriesItem.stroke }}
              />
              <span>{seriesItem.label}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="">
        <SignalChart
          rawSignal={rawSeries?.points || []}
          smoothSignal={smoothSeries?.points || []}
          showRawSignal={Boolean(rawSeries)}
          rawBorderColor={rawSeries?.stroke}
          smoothBorderColor={smoothSeries?.stroke}
          peaks={[]}
          troughs={[]}
          maxSecs={Math.max(1, Math.ceil(maxSecs || 0))}
          height={224}
          tickStepSize={maxSecs <= 10 ? 0.5 : 5}
        />
      </div>
    </section>
  );
}

export default function PatientRecordingOverlay({ entry, open, onClose }) {
  const { t } = useTranslation();
  const { formatDateTime } = useDateTimeFormat();
  const [state, setState] = useState({
    loading: false,
    error: null,
    data: null,
    variant: null,
  });

  console.log(state);

  useEffect(() => {
    if (!open || !entry?.uid || !entry?.name) {
      setState({
        loading: false,
        error: null,
        data: null,
        variant: null,
      });
      return;
    }

    let cancelled = false;

    const loadRecording = async () => {
      setState({
        loading: true,
        error: null,
        data: null,
        variant: null,
      });

      try {
        const match = await loadPatientRecordingDocument(entry);
        if (cancelled) return;

        setState({
          loading: false,
          error: null,
          data: match.data,
          variant: match.variant,
        });
      } catch (error) {
        if (cancelled) return;

        setState({
          loading: false,
          error,
          data: null,
          variant: null,
        });
      }
    };

    loadRecording();

    return () => {
      cancelled = true;
    };
  }, [entry, open]);

  const recordingTime = useMemo(
    () => formatDateTime(entry?.takenAt),
    [entry?.takenAt, formatDateTime],
  );

  const presentation = useMemo(() => {
    if (!state.data || !entry) return null;
    return buildRecordingPresentation(
      { ...entry, variant: state.variant },
      state.data,
      t,
    );
  }, [entry, state.data, state.variant, t]);

  if (!open || !entry) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label={t("profilePage.closeOverlay", "Close recording details")}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-greyscale-1 bg-container shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-greyscale-1 p-4 pb-2">
          <div className="min-w-0 w-full">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold text-typography">
                {presentation?.title ||
                  t("profilePage.recordingDetailsTitle", "Recording details")}
              </h2>
              {state.variant ? (
                <span className="rounded-full bg-greyscale-1 px-3 py-1 text-xs font-medium text-typography">
                  {state.variant}
                </span>
              ) : null}
            </div>
            <p className="text-sm text-greyscale-3">
              {recordingTime.date || "--"} {recordingTime.time || "--"}{" "}
              <span className="break-all text-xs text-greyscale-3">
                ({entry.name})
              </span>
            </p>
          </div>

          <button
            type="button"
            className="outlined-button !min-h-0 !px-4 !py-2 !text-sm"
            onClick={onClose}
          >
            {t("profilePage.closeOverlayButton", "Close")}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pt-2 pb-4">
          {state.loading ? (
            <div className="flex h-full items-center justify-center">
              <p>{t("profilePage.loadingRecording", "Loading recording…")}</p>
            </div>
          ) : state.error ? (
            <div className="flex h-full items-center justify-center">
              <p className="max-w-md text-center text-red-500">
                {t(
                  "profilePage.recordingLoadError",
                  "We couldn't load that recording. Please try again.",
                )}
              </p>
            </div>
          ) : !presentation ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-greyscale-3">
                {t(
                  "profilePage.recordingUnavailable",
                  "No preview data is available for this recording.",
                )}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <section className="flex flex-wrap justify-start gap-4">
                {presentation.statuses?.length
                  ? presentation.statuses.map((status) => (
                      <SummaryCard
                        key={status.label}
                        label={status.label}
                        value={status.value}
                        unit={status.unit}
                      />
                    ))
                  : null}

                {presentation.metrics?.length ? (
                  presentation.metrics.map((metric) => (
                    <SummaryCard
                      key={metric.label}
                      label={metric.label}
                      value={metric.value}
                      unit={metric.unit}
                    />
                  ))
                ) : (
                  <div className="mt-4 rounded-xl bg-background px-4 py-6 text-sm text-greyscale-3">
                    {t(
                      "profilePage.recordingSummaryUnavailable",
                      "This recording does not currently expose summary metrics.",
                    )}
                  </div>
                )}
              </section>

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                {presentation.charts.map((chart) => (
                  <TimeSeriesChart
                    key={chart.title}
                    title={chart.title}
                    points={chart.points}
                    series={chart.series}
                    stroke={chart.stroke}
                    emptyLabel={chart.emptyLabel}
                  />
                ))}
              </div>

              <FlowVolumeLoopsSection recordJson={state.data} />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
